import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Home, Car } from 'lucide-react';
import { getPaymentStatus } from '../lib/payments';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = React.useState({ phase: 'polling', attempts: 0, status: null });

  React.useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', message: 'No session id in URL' });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const s = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (s.payment_status === 'paid') {
          setState({ phase: 'success', status: s });
          return;
        }
        if (s.payment_status === 'failed' || s.payment_status === 'expired') {
          setState({ phase: 'failed', status: s });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: 'timeout', status: s });
          return;
        }
        setState((prev) => ({ ...prev, attempts }));
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: 'error', message: e?.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="page-payment-success">
      <div className="max-w-md w-full am-card p-8 text-center">
        {state.phase === 'polling' && (
          <>
            <Loader2 className="w-10 h-10 text-[#ff2a2a] mx-auto animate-spin" />
            <h1 className="mt-5 font-display text-3xl uppercase tracking-tight">Confirming payment</h1>
            <p className="mt-2 text-zinc-400 text-sm">Attempt {state.attempts} of {MAX_ATTEMPTS} — hang tight.</p>
          </>
        )}
        {state.phase === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h1 className="mt-5 font-display text-4xl uppercase tracking-tight">You&rsquo;re in.</h1>
            <p className="mt-2 text-zinc-400 text-sm">
              Welcome to the crew. Your subscription is active and your receipt is on the way.
            </p>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 break-all">
              {state.status?.lookup_key} · session {sessionId?.slice(-10)}
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/marketplace" className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold py-3 transition-colors inline-flex items-center justify-center gap-2" data-testid="success-marketplace">
                <Car className="w-4 h-4" /> Explore Marketplace
              </Link>
              <Link to="/" className="hairline hover:border-[#ff2a2a] text-white text-sm font-semibold py-3 transition-colors inline-flex items-center justify-center gap-2" data-testid="success-home">
                <Home className="w-4 h-4" /> Back to Home
              </Link>
            </div>
          </>
        )}
        {(state.phase === 'failed' || state.phase === 'timeout' || state.phase === 'error') && (
          <>
            <XCircle className="w-12 h-12 text-amber-400 mx-auto" />
            <h1 className="mt-5 font-display text-3xl uppercase tracking-tight">
              {state.phase === 'timeout' ? 'Still processing' : 'Payment issue'}
            </h1>
            <p className="mt-2 text-zinc-400 text-sm">
              {state.phase === 'timeout'
                ? 'Your payment is still being confirmed. Refresh this page in a minute, or check your email for the receipt.'
                : (state.message || 'The payment did not complete. No charge was made — try again anytime.')}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/pricing" className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold py-3 transition-colors">Try again</Link>
              <Link to="/" className="hairline hover:border-[#ff2a2a] text-white text-sm font-semibold py-3 transition-colors">Back to Home</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
